import React, { useState, useEffect } from 'react';
import { Button } from 'components/button';
import { pingQboCompany, disconnectQbo, reAuthenticateQbo, forceReconnectQbo, getQboTokenInfo, QboConnectionStatusResult, QboTokenInfo } from 'services/reconciliationService';
import styled from 'styled-components';
import { theme } from 'styles/theme';
import { IoRefreshOutline, IoCheckmarkCircle, IoCloseCircle, IoTimeOutline, IoLogOutOutline, IoKeyOutline, IoWarningOutline, IoClose } from 'react-icons/io5';

interface ConnectionStatusCardProps {
  className?: string;
  style?: React.CSSProperties;
}

const ConnectionStatusCard: React.FC<ConnectionStatusCardProps> = ({ className, style }) => {
  const [isPinging, setIsPinging] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<QboConnectionStatusResult | null>(null);
  const [lastPingTime, setLastPingTime] = useState<Date | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [modalData, setModalData] = useState<{
    type: 'success' | 'warning' | 'error' | 'info';
    title: string;
    message: string;
    details?: any;
    showReconnectButton?: boolean;
  } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState<{
    type: 'disconnect' | 'reauthenticate';
    title: string;
    message: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);
  const [tokenInfo, setTokenInfo] = useState<QboTokenInfo | null>(null);
  
  // Determine connection state
  const isConnected = connectionStatus?.success && connectionStatus?.data;
  const hasAttemptedPing = lastPingTime !== null;

  // Auto-check connection status when component mounts
  useEffect(() => {
    const checkInitialConnection = async () => {
      try {
        // Fetch token info first
        const tokenDetails = await getQboTokenInfo();
        setTokenInfo(tokenDetails);
        
        const result = await pingQboCompany();
        setConnectionStatus(result);
        setLastPingTime(new Date());
        
        // Only show modal if automatic refresh failed and re-authentication is needed
        if (result.tokenStatus === 'reauthentication_needed') {
          setModalData({
            type: 'warning',
            title: 'Token Refresh Failed',
            message: 'We attempted to refresh your access token automatically, but it failed. This usually means the refresh token has expired or is invalid.',
            details: {
              reason: 'Access token expired and refresh failed',
              error: result.message || 'Unknown error occurred during token refresh',
              action: 'Please click "Reconnect to QBO" to re-authenticate',
              lastAttempt: new Date().toLocaleString('en-US', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })
            },
            showReconnectButton: true
          });
          setShowStatusModal(true);
        }
        // If success is true and tokenStatus is 'connected', the automatic refresh worked - no modal needed
      } catch (error: any) {
        setConnectionStatus({
          success: false,
          error: error.response?.data?.error || error.message || 'Connection failed',
          message: error.response?.data?.message || error.message || 'Connection failed'
        });
        setLastPingTime(new Date());
      }
    };

    // Only check if we haven't attempted a ping yet
    if (!hasAttemptedPing) {
      checkInitialConnection();
    }
  }, [hasAttemptedPing]); // Include hasAttemptedPing in dependencies

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handlePing = async () => {
    if (isPinging) return;

    setIsPinging(true);

    try {
      const result = await pingQboCompany();
      setConnectionStatus(result);
      setLastPingTime(new Date());
      
      // Show current state and prompt for action if needed
      if (result.success && result.data) {
        // Connected - show success message with enhanced details
        setModalData({
          type: 'success',
          title: 'QuickBooks Online Connected',
          message: 'Your QuickBooks Online connection is working properly.',
                               details: {
                       companyName: result.data.companyName,
                       companyId: result.data.companyId,
                       realmId: result.data.realmId,
                       timezone: result.data.timezone,
                       country: result.data.country,
                       legalName: result.data.legalName,
                       lastPingTime: new Date().toLocaleString('en-US', {
                         year: 'numeric',
                         month: 'numeric',
                         day: 'numeric',
                         hour: '2-digit',
                         minute: '2-digit',
                         second: '2-digit'
                       })
                     }
        });
        setShowStatusModal(true);
      } else if (result.tokenStatus === 'reauthentication_needed') {
        // Re-authentication needed - prompt user
        setModalData({
          type: 'warning',
          title: 'Re-authentication Required',
          message: result.message || 'Token refresh failed. Your QuickBooks Online connection needs to be refreshed.',
          showReconnectButton: true
        });
        setShowStatusModal(true);
      } else if (result.tokenStatus === 'reconnection_required') {
        // Reconnection required - prompt user
        setModalData({
          type: 'error',
          title: 'Connection Broken',
          message: result.message || 'Your QuickBooks Online connection is broken and needs to be re-established.',
          showReconnectButton: true
        });
        setShowStatusModal(true);
      } else {
        // Other error states
        setModalData({
          type: 'error',
          title: 'Connection Issue Detected',
          message: result.message || 'An unknown error occurred with your QuickBooks Online connection.',
          showReconnectButton: true
        });
        setShowStatusModal(true);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Connection failed';
      setConnectionStatus({
        success: false,
        error: errorMessage,
        message: error.response?.data?.message || errorMessage
      });
      
      // Show error modal with reconnection option
      setModalData({
        type: 'error',
        title: 'Connection Failed',
        message: `Failed to connect to QuickBooks Online: ${errorMessage}`,
        showReconnectButton: true
      });
      setShowStatusModal(true);
    } finally {
      setIsPinging(false);
    }
  };

  const handleModalReconnect = () => {
    setShowStatusModal(false);
    setModalData(null);
    handleReAuthenticate();
  };

  const handleModalClose = () => {
    setShowStatusModal(false);
    setModalData(null);
  };

  const handleDisconnect = async () => {
    if (isDisconnecting) return;

    setConfirmModalData({
      type: 'disconnect',
      title: 'Disconnect from QuickBooks Online',
      message: 'Are you sure you want to disconnect?',
      description: 'This will revoke all access tokens and completely remove your QuickBooks Online connection. You will need to re-authenticate to use QBO features again.',
      onConfirm: async () => {
        setShowConfirmModal(false);
        setConfirmModalData(null);
        setIsDisconnecting(true);

        try {
          await disconnectQbo();
          setConnectionStatus(null);
          setLastPingTime(null);
          
          // Show success feedback
          setModalData({
            type: 'success',
            title: 'Successfully Disconnected',
            message: 'Your QuickBooks Online connection has been successfully disconnected. You can reconnect anytime using the "Connect to QBO" button.'
          });
          setShowStatusModal(true);
        } catch (error: any) {
          console.error('Disconnect failed:', error);
          setModalData({
            type: 'error',
            title: 'Disconnect Failed',
            message: 'Failed to disconnect: ' + (error.response?.data?.error || error.message || 'Unknown error')
          });
          setShowStatusModal(true);
        } finally {
          setIsDisconnecting(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleReAuthenticate = () => {
    setConfirmModalData({
      type: 'reauthenticate',
      title: 'Re-authenticate with QuickBooks Online',
      message: 'Are you sure you want to re-authenticate?',
      description: 'This will redirect you to QuickBooks Online to refresh your connection. Your existing tokens will be updated with new ones.',
      onConfirm: () => {
        setShowConfirmModal(false);
        setConfirmModalData(null);
        reAuthenticateQbo();
      }
    });
    setShowConfirmModal(true);
  };

  const handleForceReconnect = () => {
    setConfirmModalData({
      type: 'reauthenticate',
      title: 'Force Reconnect to QuickBooks Online',
      message: 'Are you sure you want to force reconnect?',
      description: 'This will clear all existing tokens and force a completely fresh connection to QuickBooks Online. This is useful if you\'re experiencing persistent connection issues.',
      onConfirm: () => {
        setShowConfirmModal(false);
        setConfirmModalData(null);
        forceReconnectQbo();
      }
    });
    setShowConfirmModal(true);
  };

  const handleManualDisconnect = async () => {
    if (isDisconnecting) return;

    setConfirmModalData({
      type: 'disconnect',
      title: 'Manual Disconnect from QuickBooks Online',
      message: 'Are you sure you want to manually disconnect?',
      description: 'This will completely remove all QuickBooks Online connection data for your company and force you to re-authenticate. This is useful if you\'re experiencing persistent connection issues.',
      onConfirm: async () => {
        setShowConfirmModal(false);
        setConfirmModalData(null);
        setIsDisconnecting(true);

        try {
          // First try the normal disconnect
          await disconnectQbo();
          
          // Clear local state
          setConnectionStatus(null);
          setLastPingTime(null);
          
          // Show success feedback
          setModalData({
            type: 'success',
            title: 'Successfully Disconnected',
            message: 'Your company has been successfully disconnected from QuickBooks Online. You can now reconnect using the "Connect to QBO" button.'
          });
          setShowStatusModal(true);
        } catch (error: any) {
          console.error('Manual disconnect failed:', error);
          setModalData({
            type: 'error',
            title: 'Manual Disconnect Failed',
            message: 'Disconnect failed: ' + (error.response?.data?.error || error.message || 'Unknown error')
          });
          setShowStatusModal(true);
        } finally {
          setIsDisconnecting(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusColor = () => {
    if (!connectionStatus) return '#6b7280'; // gray
    if (connectionStatus.success) return '#16a34a'; // green
    if (connectionStatus.tokenStatus === 'reauthentication_needed') return '#f59e0b'; // amber
    if (connectionStatus.tokenStatus === 'reconnection_required') return '#dc2626'; // red
    return '#dc2626'; // red
  };

  const getStatusIcon = () => {
    if (!connectionStatus) return <IoTimeOutline size={16} />;
    if (connectionStatus.success) return <IoCheckmarkCircle size={16} />;
    if (connectionStatus.tokenStatus === 'reauthentication_needed') return <IoWarningOutline size={16} />;
    if (connectionStatus.tokenStatus === 'reconnection_required') return <IoCloseCircle size={16} />;
    return <IoCloseCircle size={16} />;
  };

  const getStatusText = () => {
    if (!connectionStatus) return 'Unknown';
    if (connectionStatus.success) return 'Connected';
    if (connectionStatus.tokenStatus === 'reauthentication_needed') return 'Re-authentication Needed';
    if (connectionStatus.tokenStatus === 'reconnection_required') return 'Reconnection Required';
    if (connectionStatus.tokenStatus === 'disconnected') return 'Disconnected';
    return 'Connection Error';
  };

  return (
    <Card className={className} style={style}>
      <CardHeader onClick={handleToggleExpand}>
        <Title>QBO Connection Status</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StatusIndicator color={getStatusColor()}>
            {getStatusIcon()}
            <span>
              {getStatusText()}
            </span>
          </StatusIndicator>
          <ExpandIcon isExpanded={isExpanded}>▼</ExpandIcon>
        </div>
      </CardHeader>

      {isExpanded && (
        <>
          <CardContent>
            {connectionStatus?.data && (
              <InfoGrid>
                <InfoItem>
                  <Label>Company Name</Label>
                  <Value>{connectionStatus.data.companyName}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Company ID</Label>
                  <Value>{connectionStatus.data.companyId}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Timezone</Label>
                  <Value>{connectionStatus.data.timezone}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Country</Label>
                  <Value>{connectionStatus.data.country}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Legal Name</Label>
                  <Value>{connectionStatus.data.legalName}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Realm ID</Label>
                  <Value>{connectionStatus.data.realmId}</Value>
                </InfoItem>
              </InfoGrid>
            )}

            {!connectionStatus?.success && connectionStatus?.message && (
              <>
                {connectionStatus.tokenStatus === 'reauthentication_needed' ? (
                  <InfoMessage>
                    <strong>🔄 Re-authentication Needed:</strong> {connectionStatus.message}
                  </InfoMessage>
                ) : (
                  <ErrorMessage>
                    <strong>Connection Status:</strong> {connectionStatus.message}
                  </ErrorMessage>
                )}
                <InfoMessage>
                  <strong>💡 Tip:</strong> If you're having persistent connection issues, try "Manual Disconnect" to completely clear your company's connection data, then reconnect.
                </InfoMessage>
              </>
            )}

            {/* Token Details Section */}
            {tokenInfo && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '600', color: theme.color.text, marginBottom: '8px' }}>
                  Token Details
                </h4>
                <div style={{ 
                  background: '#f8f9fa', 
                  border: '1px solid #e9ecef', 
                  borderRadius: '6px', 
                  padding: '12px',
                  fontSize: '12px'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <span style={{ color: theme.color.subtext, fontWeight: '500' }}>Token Status:</span>
                      <span style={{ 
                        color: tokenInfo.hasToken ? (tokenInfo.isExpired ? '#f59e0b' : '#16a34a') : '#dc2626',
                        fontWeight: '600',
                        marginLeft: '4px'
                      }}>
                        {tokenInfo.hasToken ? (tokenInfo.isExpired ? 'Expired' : 'Valid') : 'No Token'}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: theme.color.subtext, fontWeight: '500' }}>Realm ID:</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '11px', marginLeft: '4px' }}>
                        {tokenInfo.realmId || 'N/A'}
                      </span>
                    </div>
                    {tokenInfo.accessTokenExpiry && (
                      <div>
                        <span style={{ color: theme.color.subtext, fontWeight: '500' }}>Access Token Expires:</span>
                        <span style={{ marginLeft: '4px' }}>
                          {new Date(tokenInfo.accessTokenExpiry).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                    {tokenInfo.refreshTokenExpiry && (
                      <div>
                        <span style={{ color: theme.color.subtext, fontWeight: '500' }}>Refresh Token Expires:</span>
                        <span style={{ marginLeft: '4px' }}>
                          {new Date(tokenInfo.refreshTokenExpiry).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                    {tokenInfo.lastRefreshed && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span style={{ color: theme.color.subtext, fontWeight: '500' }}>Last Refreshed:</span>
                        <span style={{ marginLeft: '4px' }}>
                          {new Date(tokenInfo.lastRefreshed).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {lastPingTime && (
              <LastPingInfo style={{ marginLeft: '16px' }}>
                <IoTimeOutline size={14} />
                Last ping: {lastPingTime.toLocaleString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </LastPingInfo>
            )}
          </CardContent>

          <CardActions>
            {!hasAttemptedPing ? (
              // Initial state - show Connect button
              <Button
                onClick={handleReAuthenticate}
                disabled={isPinging || isDisconnecting}
                primary
                size="small"
              >
                <IoKeyOutline size={16} />
                Connect to QBO
              </Button>
            ) : isConnected ? (
              // Connected state - show Check, Disconnect, Re-auth buttons
              <>
                <Button
                  onClick={handlePing}
                  disabled={isPinging || isDisconnecting}
                  primary
                  size="small"
                >
                  {isPinging ? (
                    <>
                      <Spinner />
                      Pinging...
                    </>
                  ) : (
                    <>
                      <IoRefreshOutline size={16} />
                      Check Connection
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleDisconnect}
                  disabled={isPinging || isDisconnecting}
                  size="small"
                  style={{ marginLeft: '8px' }}
                >
                  {isDisconnecting ? (
                    <>
                      <Spinner />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <IoLogOutOutline size={16} />
                      Disconnect
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleReAuthenticate}
                  disabled={isPinging || isDisconnecting}
                  size="small"
                  style={{ marginLeft: '8px' }}
                >
                  <IoKeyOutline size={16} />
                  Re-authenticate
                </Button>
              </>
            ) : connectionStatus?.tokenStatus === 'reauthentication_needed' ? (
              // Re-authentication needed state - show Connect prominently
              <>
                <Button
                  onClick={handlePing}
                  disabled={isPinging || isDisconnecting}
                  size="small"
                >
                  {isPinging ? (
                    <>
                      <Spinner />
                      Checking...
                    </>
                  ) : (
                    <>
                      <IoRefreshOutline size={16} />
                      Check Connection
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleReAuthenticate}
                  disabled={isPinging || isDisconnecting}
                  primary
                  size="small"
                  style={{ marginLeft: '8px' }}
                >
                  <IoKeyOutline size={16} />
                  Connect to QBO
                </Button>
                
                <Button
                  onClick={handleForceReconnect}
                  disabled={isPinging || isDisconnecting}
                  size="small"
                  style={{ marginLeft: '8px' }}
                >
                  <IoRefreshOutline size={16} />
                  Force Reconnect
                </Button>
              </>
            ) : (
              // Disconnected/Error state - show all recovery options
              <>
                <Button
                  onClick={handlePing}
                  disabled={isPinging || isDisconnecting}
                  size="small"
                >
                  {isPinging ? (
                    <>
                      <Spinner />
                      Checking...
                    </>
                  ) : (
                    <>
                      <IoRefreshOutline size={16} />
                      Check Connection
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleReAuthenticate}
                  disabled={isPinging || isDisconnecting}
                  primary
                  size="small"
                  style={{ marginLeft: '8px' }}
                >
                  <IoKeyOutline size={16} />
                  Connect to QBO
                </Button>
                
                <Button
                  onClick={handleForceReconnect}
                  disabled={isPinging || isDisconnecting}
                  size="small"
                  style={{ marginLeft: '8px' }}
                >
                  <IoRefreshOutline size={16} />
                  Force Reconnect
                </Button>
                
                <Button
                  onClick={handleManualDisconnect}
                  disabled={isPinging || isDisconnecting}
                  size="small"
                  style={{ marginLeft: '8px' }}
                >
                  {isDisconnecting ? (
                    <>
                      <Spinner />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <IoLogOutOutline size={16} />
                      Manual Disconnect
                    </>
                  )}
                </Button>
              </>
            )}
          </CardActions>
        </>
      )}

      {/* QBO Connection Status Modal */}
      {showStatusModal && modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className={`px-6 py-4 border-b ${
              modalData.type === 'success' ? 'bg-green-50 border-green-200' :
              modalData.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
              modalData.type === 'error' ? 'bg-red-50 border-red-200' :
              'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {modalData.type === 'success' && (
                    <IoCheckmarkCircle className="w-6 h-6 text-green-600" />
                  )}
                  {modalData.type === 'warning' && (
                    <IoWarningOutline className="w-6 h-6 text-yellow-600" />
                  )}
                  {modalData.type === 'error' && (
                    <IoCloseCircle className="w-6 h-6 text-red-600" />
                  )}
                  {modalData.type === 'info' && (
                    <IoTimeOutline className="w-6 h-6 text-blue-600" />
                  )}
                  <h3 className={`text-lg font-semibold ${
                    modalData.type === 'success' ? 'text-green-800' :
                    modalData.type === 'warning' ? 'text-yellow-800' :
                    modalData.type === 'error' ? 'text-red-800' :
                    'text-blue-800'
                  }`}>
                    {modalData.title}
                  </h3>
                </div>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <IoClose className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-4">{modalData.message}</p>
              
              {/* Connection Details for Success State */}
              {modalData.type === 'success' && modalData.details && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Connection Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company:</span>
                      <span className="font-medium text-gray-800">{modalData.details.companyName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">EServ Company ID:</span>
                      <span className="font-medium text-gray-800">{modalData.details.companyId}</span>
                    </div>
                    {modalData.details.qboCompanyId && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">QBO Company ID:</span>
                        <span className="font-medium text-gray-800">{modalData.details.qboCompanyId}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Realm ID:</span>
                      <span className="font-medium text-gray-800 font-mono text-xs">{modalData.details.realmId}</span>
                    </div>
                    {modalData.details.timezone && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Timezone:</span>
                        <span className="font-medium text-gray-800">{modalData.details.timezone}</span>
                      </div>
                    )}
                    {modalData.details.country && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Country:</span>
                        <span className="font-medium text-gray-800">{modalData.details.country}</span>
                      </div>
                    )}
                    {modalData.details.legalName && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Legal Name:</span>
                        <span className="font-medium text-gray-800">{modalData.details.legalName}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Check:</span>
                      <span className="font-medium text-gray-800">{modalData.details.lastPingTime}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info for Error/Warning States */}
              {(modalData.type === 'error' || modalData.type === 'warning') && (
                <>
                  {modalData.details?.error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-800">
                        <strong>Error Details:</strong> {modalData.details.error}
                      </p>
                    </div>
                  )}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Tip:</strong> If you're having persistent connection issues, try the "Force Reconnect" option in the connection panel.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3">
              {modalData.showReconnectButton ? (
                <>
                  <button
                    onClick={handleModalClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleModalReconnect}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <IoKeyOutline className="w-4 h-4 inline mr-2" />
                    Reconnect to QBO
                  </button>
                </>
              ) : (
                <button
                  onClick={handleModalClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className={`px-6 py-4 border-b ${
              confirmModalData.type === 'disconnect' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {confirmModalData.type === 'disconnect' ? (
                    <IoLogOutOutline className="w-6 h-6 text-red-600" />
                  ) : (
                    <IoKeyOutline className="w-6 h-6 text-blue-600" />
                  )}
                  <h3 className={`text-lg font-semibold ${
                    confirmModalData.type === 'disconnect' ? 'text-red-800' : 'text-blue-800'
                  }`}>
                    {confirmModalData.title}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmModalData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <IoClose className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-4 font-medium">{confirmModalData.message}</p>
              <p className="text-gray-600 text-sm mb-4">{confirmModalData.description}</p>
              
              {/* Warning for disconnect */}
              {confirmModalData.type === 'disconnect' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-800">
                    <strong>⚠️ Warning:</strong> This action cannot be undone. You will need to re-authenticate to use QuickBooks Online features again.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmModalData(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModalData.onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                  confirmModalData.type === 'disconnect' 
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                }`}
              >
                {confirmModalData.type === 'disconnect' ? (
                  <>
                    <IoLogOutOutline className="w-4 h-4 inline mr-2" />
                    Disconnect
                  </>
                ) : (
                  <>
                    <IoKeyOutline className="w-4 h-4 inline mr-2" />
                    Continue
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.color.text};
`;

const StatusIndicator = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.color};
`;

const CardContent = styled.div`
  padding: 16px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Label = styled.span`
  font-size: 11px;
  color: ${theme.color.subtext};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const Value = styled.span`
  font-size: 13px;
  color: ${theme.color.text};
  font-weight: 500;
  word-break: break-word;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const InfoMessage = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const LastPingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${theme.color.subtext};
  margin-bottom: 16px;
`;

const CardActions = styled.div`
  padding: 12px 16px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
`;

const Spinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 6px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.isExpanded ? '180deg' : '0deg'});
  color: #6b7280;
`;

export default ConnectionStatusCard; 